/**
 * FilingTrackerScreen — which GSTR-1 and GSTR-3B returns are filed, pending or
 * overdue, against the statutory monthly due dates (the 11th and the 20th of
 * the following month).
 *
 * Answers the question a shopkeeper actually asks at the end of a quarter:
 * "what have I missed?". Computed entirely from local WatermelonDB — no network
 * required, so it still answers during an outage.
 *
 * Ported from BigxBookDesktop/src/screens/reports/FilingTrackerScreen.tsx and
 * watched by the drift-check. Identical apart from the PDF footer, which names
 * the product that generated the report.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { escapeHtml } from '@/utils/escapeHtml';
import { loadFilingTracker } from '@/reports/filingTracker/filingTrackerDataSource';
import {
  buildFilingTracker,
  type FilingTracker,
  type FilingStatus,
  type ReturnCell,
} from '@/reports/filingTracker/filingTracker';

const fmtDate = (d: Date | null) => (d ? d.toLocaleDateString('en-IN') : '—');

/** 'YYYY-MM' to 'Aug 2026'. */
function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FilingTrackerScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [data, setData] = useState<FilingTracker | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      setData(buildFilingTracker(await loadFilingTracker(activeStoreId)));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId]);

  useEffect(() => { void load(); }, [load]);

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      // One row per return, not per period — a filing is the unit an accountant
      // chases, and it keeps the sheet sortable by status.
      const flat = data.rows.flatMap((r) => [
        { period: r.period, returnType: 'GSTR-1', ...r.gstr1 },
        { period: r.period, returnType: 'GSTR-3B', ...r.gstr3b },
      ]);
      const csv = toCsv(flat, [
        { header: 'Period', value: (r) => periodLabel(r.period) },
        { header: 'Return', value: (r) => r.returnType },
        { header: 'Status', value: (r) => r.status },
        { header: 'Due date', value: (r) => r.dueDate.toLocaleDateString('en-IN') },
        { header: 'Filed on', value: (r) => fmtDate(r.filedAt) },
        { header: 'ARN', value: (r) => r.arn ?? '' },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `FilingTracker_${new Date().toISOString().slice(0, 10)}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      await pdf.saveFromHtml({
        html: buildFilingHtml(data),
        defaultFileName: `FilingTracker_${new Date().toISOString().slice(0, 10)}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-body text-fg-secondary">
          GSTR-1 is due on the 11th, GSTR-3B on the 20th of the following month.
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <KPI label="Filed" value={String(data.summary.filed)} tone="ok" />
            <KPI label="Pending" value={String(data.summary.pending)} tone="warn" />
            <KPI label="Overdue" value={String(data.summary.overdue)} tone="danger" />
          </div>

          <div className="min-h-0 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
            <table className="w-full text-body">
              <thead className="sticky top-0 bg-bg-surface text-label uppercase text-fg-tertiary">
                <tr className="border-b border-stroke-hairline/10">
                  <th className="px-4 py-2 text-left font-normal">Period</th>
                  <th className="px-4 py-2 text-left font-normal">GSTR-1</th>
                  <th className="px-4 py-2 text-left font-normal">GSTR-3B</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.period} className="border-b border-stroke-hairline/10">
                    <td className="px-4 py-2.5 text-fg-primary">{periodLabel(r.period)}</td>
                    <td className="px-4 py-2.5"><Cell cell={r.gstr1} /></td>
                    <td className="px-4 py-2.5"><Cell cell={r.gstr3b} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Shared UI ───────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<FilingStatus, { cls: string; Icon: typeof CheckCircle2 }> = {
  FILED: { cls: 'text-success-ink', Icon: CheckCircle2 },
  PENDING: { cls: 'text-warning-ink', Icon: Clock },
  OVERDUE: { cls: 'text-danger-ink', Icon: AlertTriangle },
};

function Cell({ cell }: { cell: ReturnCell }) {
  const { cls, Icon } = STATUS_STYLE[cell.status];
  const label = cell.status === 'FILED' ? 'Filed' : cell.status === 'OVERDUE' ? 'Overdue' : 'Pending';
  return (
    <div className="flex flex-col gap-0.5">
      <span className={cn('flex items-center gap-1.5 font-medium', cls)}>
        <Icon size={12} className="shrink-0" />
        {label}
      </span>
      <span className="text-label text-fg-tertiary">
        {cell.status === 'FILED'
          ? `${fmtDate(cell.filedAt)}${cell.arn ? ` · ${cell.arn}` : ''}`
          : `due ${cell.dueDate.toLocaleDateString('en-IN')}`}
      </span>
    </div>
  );
}

function KPI({ label, value, tone }: { label: string; value: string; tone: 'ok' | 'warn' | 'danger' }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn(
        'mt-1 text-heading tabular-nums',
        tone === 'ok' ? 'text-success-ink' : tone === 'warn' ? 'text-warning-ink' : 'text-danger-ink',
      )}>
        {value}
      </div>
    </div>
  );
}

// ─── PDF ─────────────────────────────────────────────────────────────────────

function buildFilingHtml(d: FilingTracker): string {
  // ARN comes back from the GSTN portal and is stored verbatim, so it is
  // escaped like any other value that did not originate in this codebase.
  const cellText = (c: ReturnCell) =>
    c.status === 'FILED'
      ? `Filed · ${fmtDate(c.filedAt)}${c.arn ? ` · ${escapeHtml(c.arn)}` : ''}`
      : `${c.status === 'OVERDUE' ? 'OVERDUE' : 'Pending'} · due ${c.dueDate.toLocaleDateString('en-IN')}`;

  const rows = d.rows.map((r) => `<tr>
    <td>${escapeHtml(periodLabel(r.period))}</td>
    <td${r.gstr1.status === 'OVERDUE' ? ' style="color:#b45309"' : ''}>${cellText(r.gstr1)}</td>
    <td${r.gstr3b.status === 'OVERDUE' ? ' style="color:#b45309"' : ''}>${cellText(r.gstr3b)}</td>
  </tr>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:10px}th,td{padding:5px 8px;border-bottom:1px solid #eee}
th{text-align:left;color:#666;font-weight:500}
.kpi-grid{display:flex;gap:24px;margin:12px 0}.kpi{min-width:110px}.kpi-label{font-size:11px;text-transform:uppercase;color:#888}.kpi-value{font-size:18px;font-weight:700}</style></head>
<body>
<h1>GST Filing Tracker</h1>
<p style="color:#888">GSTR-1 due the 11th &middot; GSTR-3B due the 20th of the following month</p>
<div class="kpi-grid">
  <div class="kpi"><div class="kpi-label">Filed</div><div class="kpi-value" style="color:#15803d">${d.summary.filed}</div></div>
  <div class="kpi"><div class="kpi-label">Pending</div><div class="kpi-value" style="color:#b45309">${d.summary.pending}</div></div>
  <div class="kpi"><div class="kpi-label">Overdue</div><div class="kpi-value" style="color:#b91c1c">${d.summary.overdue}</div></div>
</div>
<table>
  <tr><th>Period</th><th>GSTR-1</th><th>GSTR-3B</th></tr>
  ${rows}
</table>
<p style="margin-top:40px;font-size:11px;color:#aaa">Generated by BigxBook on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
